import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, Container,
  CircularProgress, Snackbar, Alert
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DiagnosticService from '../services/DiagnosticService';
import { PasskeyService } from '../services/PasskeyService';

const LoginPage: React.FC = () => {
  const {
    isAuthenticated, loading: authLoading, email, setEmail,
    loginWithEmail, loginWithPasskey, verifyToken, resendToken,
    emailSubmitted, setEmailSubmitted, passkeyAvailable, reloadAuthStatus
  } = useAuth();

  const navigate = useNavigate();
  const [tokenInput, setTokenInput] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);

  // Turnstile throws "Nothing to reset found for provided container" when the widget's
  // container has been unmounted — which happens whenever the form moves to the code
  // step, and on every StrictMode effect re-run in dev. Only reset a widget that is
  // still on screen; otherwise drop the stale id and let the effect render a new one.
  const resetTurnstile = () => {
    const widgetId = turnstileWidgetId.current;
    if (!window.turnstile || !widgetId) return;
    const container = turnstileRef.current;
    if (!container || !document.body.contains(container)) {
      turnstileWidgetId.current = null;
      return;
    }
    try {
      window.turnstile.reset(widgetId);
    } catch {
      turnstileWidgetId.current = null;
    }
  };
  const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false);
  const [hasRegisteredPasskeys, setHasRegisteredPasskeys] = useState(false);
  const [checkingPasskeys, setCheckingPasskeys] = useState(false);
  const [authenticatingPasskey, setAuthenticatingPasskey] = useState(false);
  const [processingEmailFallback, setProcessingEmailFallback] = useState(false);
  const conditionalAuthStarted = useRef(false);
  const conditionalAuthAbortController = useRef<AbortController | null>(null);

  // Redirect if authenticated
  useEffect(() => {
    if (isAuthenticated) navigate('/home');
  }, [isAuthenticated, navigate]);

  // Load Turnstile script
  useEffect(() => {
    if (isAuthenticated) return;
    if (window.turnstile) { setTurnstileLoaded(true); return; }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => setTurnstileLoaded(true);
    script.onerror = () => {
      DiagnosticService.logTurnstileIssue('script_load_failed', { protocol: window.location.protocol });
      setErrorMessage('Security verification service failed to load. Please refresh the page.');
    };
    document.head.appendChild(script);
  }, [isAuthenticated]);

  // Initialize Turnstile widget
  useEffect(() => {
    if (!turnstileLoaded || isAuthenticated) return;
    let isMounted = true;

    const initializeTurnstile = () => {
      if (!isMounted || !window.turnstile || !turnstileRef.current) return;
      try {
        if (turnstileWidgetId.current) {
          // The previous widget's container is already gone (StrictMode re-run, or the
          // form moved to the code step), so remove() is the cleanup — reset() throws
          // "Nothing to reset found for provided container" on a detached container.
          try {
            window.turnstile.remove(turnstileWidgetId.current);
          } catch {
            // Widget was already torn down with its container
          }
          turnstileWidgetId.current = null;
        }
        if (turnstileRef.current) turnstileRef.current.innerHTML = '';

        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: window.APP_CONFIG?.turnstileSiteKey || "1x00000000000000000000AA",
          callback: (token: string) => { if (isMounted) setTurnstileToken(token); },
          'expired-callback': () => { if (isMounted) setTurnstileToken(null); },
          'error-callback': () => { if (isMounted) { setErrorMessage('Security verification failed.'); setTurnstileToken(null); } },
          theme: 'light'
        });
      } catch (error) {
        console.error('Error rendering Turnstile:', error);
      }
    };

    setTimeout(initializeTurnstile, 100);
    return () => { isMounted = false; };
  }, [turnstileLoaded, isAuthenticated, emailSubmitted]);

  // Conditional passkey auth
  useEffect(() => {
    if (isAuthenticated || !passkeyAvailable || conditionalAuthStarted.current) return;
    conditionalAuthStarted.current = true;

    const startConditionalAuth = async () => {
      const abortController = await PasskeyService.startConditionalAuthentication(
        async (authResponse) => {
          setAuthenticatingPasskey(true);
          const result = await PasskeyService.verifyConditionalAuthentication(authResponse);
          if (result.success) {
            await reloadAuthStatus();
            navigate('/home');
          } else {
            setErrorMessage(result.message);
            setAuthenticatingPasskey(false);
          }
        },
        (error) => setErrorMessage(error)
      );
      conditionalAuthAbortController.current = abortController;
    };

    startConditionalAuth();
    return () => { conditionalAuthAbortController.current?.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, passkeyAvailable, navigate]);

  useEffect(() => {
    return () => { conditionalAuthStarted.current = false; };
  }, []);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setErrorMessage('Please enter your email address'); return; }
    if (!turnstileToken) { setErrorMessage('Please complete the CAPTCHA verification'); return; }

    if (passkeyAvailable) {
      setCheckingPasskeys(true);
      const hasPasskeys = await PasskeyService.hasPasskeys(email);
      if (hasPasskeys) {
        setCheckingPasskeys(false);
        setHasRegisteredPasskeys(true);
        setShowPasskeyPrompt(true);
        setSuccessMessage('We found passkeys for this account.');
        return;
      }
    }

    const result = await loginWithEmail(email, turnstileToken);
    setCheckingPasskeys(false);
    if (result.success) {
      setSuccessMessage(result.message);
      setTurnstileToken(null);
      resetTurnstile();
    } else {
      setErrorMessage(result.message);
    }
  };

  const handleTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tokenInput.trim()) { setErrorMessage('Please enter the verification token'); return; }

    const result = await verifyToken(tokenInput);
    if (result.success) {
      setTokenInput('');
      navigate('/home');
    } else {
      setErrorMessage(result.message);
    }
  };

  const handlePasskeyLogin = async () => {
    setAuthenticatingPasskey(true);
    setErrorMessage(null);
    const result = await loginWithPasskey(email);
    if (result.success) {
      navigate('/home');
    } else {
      setErrorMessage(result.message);
      setAuthenticatingPasskey(false);
    }
  };

  const handleUseEmailInstead = async () => {
    setProcessingEmailFallback(true);
    setShowPasskeyPrompt(false);
    const result = await loginWithEmail(email, turnstileToken!);
    setProcessingEmailFallback(false);
    if (result.success) {
      setSuccessMessage(result.message);
      setTurnstileToken(null);
      resetTurnstile();
    } else {
      setErrorMessage(result.message);
    }
  };

  const handleCloseMessage = () => { setErrorMessage(null); setSuccessMessage(null); };

  if (authLoading || (authenticatingPasskey && isAuthenticated) || processingEmailFallback || checkingPasskeys) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
        <Typography variant="h6" ml={2}>
          {authLoading ? 'Checking authentication...' :
           processingEmailFallback ? 'Sending verification code...' :
           checkingPasskeys ? 'Checking for passkeys...' : 'Signing you in...'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Container component="main" sx={{ flexGrow: 1, py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 500, mx: 'auto' }}>
          {!emailSubmitted && !showPasskeyPrompt ? (
            <>
              <Typography variant="h5" gutterBottom>Records Portal Login</Typography>
              <Typography variant="body1" mb={3}>
                Please enter your email address to receive a one-time verification token.
              </Typography>
              <form onSubmit={handleEmailSubmit}>
                <TextField
                  fullWidth label="Email Address" type="email" name="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  margin="normal" required
                  inputProps={{ autoComplete: 'username webauthn' }}
                />
                <Box sx={{ my: 2, minHeight: '65px' }}>
                  {!turnstileLoaded ? (
                    <Box display="flex" alignItems="center" justifyContent="center" height="65px">
                      <CircularProgress size={20} />
                      <Typography variant="body2" ml={1}>Loading security verification...</Typography>
                    </Box>
                  ) : (
                    <div ref={turnstileRef} />
                  )}
                </Box>
                <Button type="submit" fullWidth variant="contained" color="primary"
                        sx={{ mt: 3, mb: 2 }} disabled={!turnstileToken || !turnstileLoaded}>
                  Continue
                </Button>
              </form>
            </>
          ) : showPasskeyPrompt ? (
            <>
              <Typography variant="h5" gutterBottom>Sign In with Passkey</Typography>
              <Typography variant="body1" mb={3}>Use your passkey to sign in to {email}</Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Button fullWidth variant="contained" color="primary" onClick={handlePasskeyLogin}
                        disabled={authenticatingPasskey}
                        startIcon={authenticatingPasskey ? <CircularProgress size={20} /> : <FingerprintIcon />}
                        size="large">
                  {authenticatingPasskey ? 'Authenticating...' : 'Use Passkey'}
                </Button>
                <Button fullWidth variant="outlined" onClick={handleUseEmailInstead} disabled={authenticatingPasskey}>
                  Use Email Verification Instead
                </Button>
                <Button fullWidth variant="text" onClick={() => {
                  setShowPasskeyPrompt(false); setHasRegisteredPasskeys(false); setEmail(''); setTurnstileToken(null);
                  resetTurnstile();
                }}>
                  Use a Different Email
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="h5" gutterBottom>Verify Your Email</Typography>
              <Typography variant="body1" mb={3}>We've sent a verification token to {email}.</Typography>
              <form onSubmit={handleTokenSubmit}>
                <TextField fullWidth label="Verification Token" value={tokenInput}
                           onChange={(e) => setTokenInput(e.target.value)} margin="normal" required />
                <Button type="submit" fullWidth variant="contained" color="primary" sx={{ mt: 3, mb: 2 }}>
                  Verify
                </Button>
                <Button fullWidth variant="text" onClick={() => resendToken().then(r => r.success ? setSuccessMessage(r.message) : setErrorMessage(r.message))} sx={{ mb: 2 }}>
                  Resend Token
                </Button>
                <Button fullWidth variant="text" onClick={() => {
                  setEmailSubmitted(false); setEmail(''); setTurnstileToken(null);
                  resetTurnstile();
                }}>
                  Use a Different Email
                </Button>
              </form>
            </>
          )}
        </Paper>
      </Container>
      <Footer />
      <Snackbar open={!!errorMessage} autoHideDuration={6000} onClose={handleCloseMessage} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseMessage} severity="error" sx={{ width: '100%' }}>{errorMessage}</Alert>
      </Snackbar>
      <Snackbar open={!!successMessage} autoHideDuration={6000} onClose={handleCloseMessage} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseMessage} severity="success" sx={{ width: '100%' }}>{successMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LoginPage;
